import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { createReservation, checkOverlap } from "../../api/resourceMngApi";
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker";
import "react-datepicker/dist/react-datepicker.css";
import ResourcePicker from "../resource/ResourcePicker";
import ResourceDetailModal from "../resource/ResourceDetailModal";

const toDateText = (d) => {
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toTimeText = (d) => {
  if (!d) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}:00`;
};

const roundToInterval = (date, interval = 10) => {
  const ms = 1000 * 60 * interval;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};

function ReservationCard() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // 활성 탭
  const [activeTab, setActiveTab] = useState('meeting');

  // 탭 변경 시 입력값 초기화 함수
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const now = new Date();
    if (tab === 'meeting') {
      setMeetSubject('');
      setMeetStart(roundToInterval(now, 10));
      setMeetEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setMeetAttendees('');
      setMeetResourceId('');
      setMeetResourceName('');
      setMeetResourceColor('');
      setMeetResourceCategory('');
      setMeetApproverId('');
      setMeetApproverName('');
    } else {
      setCarSubject('');
      setCarStart(roundToInterval(now, 10));
      setCarEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setCarAttendees('');
      setCarResourceId('');
      setCarResourceName('');
      setCarResourceColor('');
      setCarResourceCategory('');
      setCarApproverId('');
      setCarApproverName('');
    }
  };

  // ResourcePicker / ResourceDetailModal 공용 상태
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFor, setPickerFor] = useState('meeting');
  const [detailOpen, setDetailOpen] = useState(false);

  // ── 회의실 상태 ──────────────────────────────────────────
  const [meetSubject, setMeetSubject] = useState('');
  const [meetStart, setMeetStart] = useState(() => roundToInterval(new Date(), 10));
  const [meetEnd, setMeetEnd] = useState(() => roundToInterval(new Date(Date.now() + 60 * 60 * 1000), 10));
  const [meetAttendees, setMeetAttendees] = useState('');
  const [meetResourceId, setMeetResourceId] = useState('');
  const [meetResourceName, setMeetResourceName] = useState('');
  const [meetResourceColor, setMeetResourceColor] = useState('');
  const [meetResourceCategory, setMeetResourceCategory] = useState('');
  const [meetApproverId, setMeetApproverId] = useState('');
  const [meetApproverName, setMeetApproverName] = useState('');

  // ── 차량 상태 ────────────────────────────────────────────
  const [carSubject, setCarSubject] = useState('');
  const [carStart, setCarStart] = useState(() => roundToInterval(new Date(), 10));
  const [carEnd, setCarEnd] = useState(() => roundToInterval(new Date(Date.now() + 60 * 60 * 1000), 10));
  const [carAttendees, setCarAttendees] = useState('');
  const [carResourceId, setCarResourceId] = useState('');
  const [carResourceName, setCarResourceName] = useState('');
  const [carResourceColor, setCarResourceColor] = useState('');
  const [carResourceCategory, setCarResourceCategory] = useState('');
  const [carApproverId, setCarApproverId] = useState('');
  const [carApproverName, setCarApproverName] = useState('');

  // ResourcePicker에서 자원 선택 시
  const handleResourceSelect = (node) => {
    if (pickerFor === 'meeting') {
      setMeetResourceId(node.id);
      setMeetResourceName(node.res_name);
      setMeetResourceColor(node.res_color || '');
      setMeetResourceCategory(node.res_category || '');
      setMeetApproverId(node.approver_id || '');
      setMeetApproverName(node.approver_name || '');
    } else {
      setCarResourceId(node.id);
      setCarResourceName(node.res_name);
      setCarResourceColor(node.res_color || '');
      setCarResourceCategory(node.res_category || '');
      setCarApproverId(node.approver_id || '');
      setCarApproverName(node.approver_name || '');
    }
    setPickerOpen(false);
  };

  // 회의실 중복확인
  const handleMeetingCheckOverlap = async () => {
    if (!meetResourceId) {
      alert("자원을 먼저 선택하세요.");
      return;
    }
    if (!meetStart || !meetEnd) {
      alert("시작/종료 일시를 선택하세요.");
      return;
    }
    const now = new Date();
    if (meetStart <= now) {
      alert("시작일시는 현재 시각보다 늦어야 합니다.");
      return;
    }
    if (meetEnd <= meetStart) {
      alert("종료일시는 시작일시보다 늦어야 합니다.");
      return;
    }
    const payload = {
      res_id: meetResourceId,
      res_st_date: toDateText(meetStart),
      res_st_time: toTimeText(meetStart),
      res_ed_date: toDateText(meetEnd),
      res_ed_time: toTimeText(meetEnd)
    };
    try {
      const data = await checkOverlap(payload);
      if (data.overlaps && data.overlaps.length > 0) {
        alert("이미 예약된 시간대입니다.");
      } else {
        alert("예약 가능합니다.");
      }
    } catch (err) {
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  // 차량 중복확인
  const handleCarCheckOverlap = async () => {
    if (!carResourceId) {
      alert("자원을 먼저 선택하세요.");
      return;
    }
    if (!carStart || !carEnd) {
      alert("시작/종료 일시를 선택하세요.");
      return;
    }
    const now = new Date();
    if (carStart <= now) {
      alert("시작일시는 현재 시각보다 늦어야 합니다.");
      return;
    }
    if (carEnd <= carStart) {
      alert("종료일시는 시작일시보다 늦어야 합니다.");
      return;
    }
    const payload = {
      res_id: carResourceId,
      res_st_date: toDateText(carStart),
      res_st_time: toTimeText(carStart),
      res_ed_date: toDateText(carEnd),
      res_ed_time: toTimeText(carEnd)
    };
    try {
      const data = await checkOverlap(payload);
      if (data.overlaps && data.overlaps.length > 0) {
        alert("이미 예약된 시간대입니다.");
      } else {
        alert("예약 가능합니다.");
      }
    } catch (err) {
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };

  // 회의실 저장
  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    if (!meetSubject.trim()) { alert("제목을 입력하세요."); return; }
    if (!meetStart || !meetEnd) { alert("예약일시를 선택하세요."); return; }
    if (meetEnd <= meetStart) { alert("종료일시는 시작일시보다 늦어야 합니다."); return; }
    if (meetStart <= new Date()) { alert("시작일시는 현재 시각보다 늦어야 합니다."); return; }
    if (!meetAttendees || parseInt(meetAttendees, 10) <= 0) { alert("인원수를 입력하세요."); return; }
    if (!meetResourceId) { alert("자원을 선택하세요."); return; }
    try {
      await createReservation({
        subject: meetSubject,
        res_id: meetResourceId,
        res_name: meetResourceName,
        res_color: meetResourceColor,
        resCategory: meetResourceCategory,
        approver_id: meetApproverId,
        approver_name: meetApproverName,
        writer_id: user?.userid,
        writer_name: user?.name,
        res_st_date: toDateText(meetStart),
        res_st_time: toTimeText(meetStart),
        res_ed_date: toDateText(meetEnd),
        res_ed_time: toTimeText(meetEnd),
        member_cnt: parseInt(meetAttendees, 10),
        comment: '',
        status: "승인"
      }, token);
      alert("등록되었습니다.");
      const now = new Date();
      setMeetSubject('');
      setMeetStart(roundToInterval(now, 10));
      setMeetEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setMeetAttendees('');
      setMeetResourceId('');
      setMeetResourceName('');
      setMeetResourceColor('');
      setMeetResourceCategory('');
      setMeetApproverId('');
      setMeetApproverName('');
    } catch (err) {
      if (err.response?.status === 409) {
        alert(err.response.data.message || "이미 예약된 시간대입니다.");
        return;
      }
      alert("예약 등록 중 오류가 발생했습니다.");
    }
  };

  // 차량 저장
  const handleCarSubmit = async (e) => {
    e.preventDefault();
    if (!carSubject.trim()) { alert("제목을 입력하세요."); return; }
    if (!carStart || !carEnd) { alert("예약일시를 선택하세요."); return; }
    if (carEnd <= carStart) { alert("종료일시는 시작일시보다 늦어야 합니다."); return; }
    if (carStart <= new Date()) { alert("시작일시는 현재 시각보다 늦어야 합니다."); return; }
    if (!carAttendees || parseInt(carAttendees, 10) <= 0) { alert("인원수를 입력하세요."); return; }
    if (!carResourceId) { alert("자원을 선택하세요."); return; }
    try {
      await createReservation({
        subject: carSubject,
        res_id: carResourceId,
        res_name: carResourceName,
        res_color: carResourceColor,
        resCategory: carResourceCategory,
        approver_id: carApproverId,
        approver_name: carApproverName,
        writer_id: user?.userid,
        writer_name: user?.name,
        res_st_date: toDateText(carStart),
        res_st_time: toTimeText(carStart),
        res_ed_date: toDateText(carEnd),
        res_ed_time: toTimeText(carEnd),
        member_cnt: parseInt(carAttendees, 10),
        comment: '',
        status: "대기"
      }, token);
      alert("관리자에게 승인요청하였습니다.");
      const now = new Date();
      setCarSubject('');
      setCarStart(roundToInterval(now, 10));
      setCarEnd(roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10));
      setCarAttendees('');
      setCarResourceId('');
      setCarResourceName('');
      setCarResourceColor('');
      setCarResourceCategory('');
      setCarApproverId('');
      setCarApproverName('');
    } catch (err) {
      if (err.response?.status === 409) {
        alert(err.response.data.message || "이미 예약된 시간대입니다.");
        return;
      }
      alert("예약 등록 중 오류가 발생했습니다.");
    }
  };

  const currentDetailResourceId = activeTab === 'meeting' ? meetResourceId : carResourceId;

  return (
    <div className="card portal-card reservation-quick-card flex-grow-1">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="card-title mb-0">빠른 자원 예약</h4>
          <button 
            type="button" 
            className="btn btn-outline-primary btn-sm"
            onClick={() => navigate('/reservations/calendar?status=active')}
          >
            예약보기
          </button>
        </div>
        <ul className="nav nav-pills bg-light rounded" role="tablist">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link${activeTab === 'meeting' ? ' active' : ''}`}
              onClick={() => handleTabChange('meeting')}
            >
              회의실
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link${activeTab === 'car' ? ' active' : ''}`}
              onClick={() => handleTabChange('car')}
            >
              차량
            </button>
          </li>
        </ul>
        <div className="tab-content mt-3" style={{ minHeight: "245px" }}>
          {activeTab === 'meeting' && (
            <form onSubmit={handleMeetingSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="text"
                  value={meetSubject}
                  onChange={e => setMeetSubject(e.target.value)}
                  placeholder="제목을 입력하세요."
                />
              </div>
              <div className="d-flex align-items-center gap-2 mb-3 reservation-resource-row" style={{marginTop: '16px'}}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={meetStart}
                    onChange={setMeetStart}
                    placeholder="시작일시"
                    showTime={true}
                  />
                </div>
                <span>~</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={meetEnd}
                    onChange={setMeetEnd}
                    placeholder="종료일시"
                    showTime={true}
                    minDate={meetStart}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={handleMeetingCheckOverlap}
                >
                  중복 확인
                </button>
              </div>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  style={{ width: '50%' }}
                  value={meetAttendees}
                  onChange={e => setMeetAttendees(e.target.value)}
                  placeholder="인원수 (예: 10)"
                />
              </div>
              <div className="d-flex align-items-center gap-2 mb-2" style={{marginTop: '16px'}}>
                <div
                  style={{
                    backgroundColor: meetResourceColor || '#1edc71',
                    width: '40px',
                    height: '28px',
                    borderRadius: '4px',
                    flexShrink: 0,
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: '#fff',
                    fontWeight: 600
                  }}
                >
                  색상
                </div>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: '50%', marginBottom: 0 }}
                  value={meetResourceName}
                  readOnly
                  placeholder="자원을 선택하세요."
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => { setPickerFor('meeting'); setPickerOpen(true); }}
                >
                  찾기
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setDetailOpen(true)}
                  disabled={!meetResourceId}
                >
                  상세조회
                </button>
              </div>
              <div className="text-center mt-3">
                <button type="submit" className="btn btn-success w-md">등록</button>
              </div>
            </form>
          )}
          {activeTab === 'car' && (
            <form onSubmit={handleCarSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="text"
                  value={carSubject}
                  onChange={e => setCarSubject(e.target.value)}
                  placeholder="제목을 입력하세요."
                />
              </div>
              <div className="d-flex align-items-center gap-2 mb-3" style={{marginTop: '16px'}}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={carStart}
                    onChange={setCarStart}
                    placeholder="시작일시"
                    showTime={true}
                  />
                </div>
                <span>~</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CommonDatePicker
                    inputClassName="compact-date-input"
                    wrapperClassName="compact-date-wrapper"
                    value={carEnd}
                    onChange={setCarEnd}
                    placeholder="종료일시"
                    showTime={true}
                    minDate={carStart}
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={handleCarCheckOverlap}
                >
                  중복 확인
                </button>
              </div>
              <div className="mb-2">
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  style={{ width: '50%' }}
                  value={carAttendees}
                  onChange={e => setCarAttendees(e.target.value)}
                  placeholder="인원수 (예: 10)"
                />
              </div>
              <div className="d-flex align-items-center gap-2 mb-2 flex-nowrap" style={{marginTop: '16px'}}>
                <div
                  style={{
                    backgroundColor: carResourceColor || '#1edc71',
                    width: '40px',
                    height: '28px',
                    borderRadius: '4px',
                    flexShrink: 0,
                    border: '1px solid #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: '#fff',
                    fontWeight: 600
                  }}
                >
                  색상
                </div>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: '50%', marginBottom: 0 }}
                  value={carResourceName}
                  readOnly
                  placeholder="자원을 선택하세요."
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => { setPickerFor('car'); setPickerOpen(true); }}
                >
                  찾기
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setDetailOpen(true)}
                  disabled={!carResourceId}
                >
                  상세조회
                </button>
              </div>
              <div className="text-center mt-3">
                <button type="submit" className="btn btn-success w-md">등록</button>
              </div>
            </form>
          )}
        </div>
      </div>
      {pickerOpen && (
        <ResourcePicker
          allowedCategory={pickerFor === 'meeting' ? '회의실' : '차량'}
          onSelect={handleResourceSelect}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {detailOpen && currentDetailResourceId && (
        <ResourceDetailModal
          resourceId={currentDetailResourceId}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

export default ReservationCard;